import React, { useRef, useState } from 'react';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import ChartCard, { ChartCardIconButton, ChartCardButton } from '../shared/ChartCard.js';
import SegmentedToggle from '../shared/SegmentedToggle.js';
import { buildExportSvg, downloadSvg } from '../shared/svgExport.js';
import { FONT_STACK, DARK_TEXT } from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

const RATE_COLORS = {
    A: '#00bf63',
    B: '#c7de26',
    C: '#f6cc27',
    D: '#fc7c2c',
    E: '#ff5a3d',
    F: '#ff1b3b',
    G: '#c0003f',
};

const FREQUENTATION_COLORS = {
    high: '#fc7c2c',
    average: '#f6cc27',
    low: '#c7de26',
};

const FREQUENTATION_LABELS = {
    high: 'Forte',
    average: 'Moyenne',
    low: 'Faible',
};

// Couleurs de fond/bordure fixes (hex, pas var(--...)) pour le rendu SVG du mode
// Export : une fois le SVG cloné/téléchargé hors de la page (buildExportSvg), les
// custom properties CSS de l'app ne sont plus résolubles — mêmes valeurs que
// --color-surface-low/--color-surface-high dans index.css, en dur.
const HEADER_BG = '#f1f3fc';
const BORDER_COLOR = '#e8eaf3';

// "Nom de la salle" reste toujours affiché (identifiant de ligne) ; les 4 autres sont
// masquables individuellement depuis la barre de paramétrage.
const COLUMNS = [
    { key: 'name_room', label: 'Nom de la salle', weight: 2, align: 'left', alwaysVisible: true },
    { key: 'nb_equipments', label: "Nombre d'équipements", weight: 1.3, align: 'center' },
    { key: 'rate_letter', label: 'Note moyenne', weight: 1, align: 'center', badge: 'rate' },
    { key: 'frequentation', label: 'Fréquentation', weight: 1.2, align: 'center', badge: 'frequentation' },
    { key: 'has_awareness_poster', label: 'Affiche de sensibilisation', weight: 1.6, align: 'center' },
];

const DEFAULT_SETTINGS = {
    tableWidth: 840,
    rowHeight: 34,
    fontSize: 13,
};

const SETTINGS_SECTIONS = [
    {
        id: 'dimensions',
        title: 'Dimensions',
        fields: [
            { key: 'tableWidth', label: 'Largeur du tableau', type: 'range', min: 400, max: 1000, step: 20, unit: 'px' },
            { key: 'rowHeight', label: 'Hauteur des lignes', type: 'range', min: 24, max: 48, step: 2, unit: 'px' },
        ],
    },
    {
        id: 'typography',
        title: 'Typographie',
        fields: [{ key: 'fontSize', label: 'Taille de police', type: 'range', min: 9, max: 16, step: 1, unit: 'px' }],
    },
];

const SettingsField = ({ field, settings, onChange }) => (
    <div className="sunburst-settings-row" key={field.key}>
        <label htmlFor={`room-table-setting-${field.key}`}>{field.label}</label>
        <div className="sunburst-settings-range">
            <input
                id={`room-table-setting-${field.key}`}
                type="range"
                min={field.min}
                max={field.max}
                step={field.step}
                value={settings[field.key]}
                onChange={(e) => onChange(field.key, Number(e.target.value))}
            />
            <span className="sunburst-settings-value">
                {settings[field.key]}
                {field.unit}
            </span>
        </div>
    </div>
);

const SettingsPanel = ({ settings, onChange, onClose }) => (
    <div className="sunburst-settings-panel">
        <div className="sunburst-settings-panel-header">
            <span>Réglages d'affichage</span>
            <button type="button" className="sunburst-settings-close" onClick={onClose} aria-label="Fermer">
                <FaTimes />
            </button>
        </div>
        <div className="sunburst-settings-grid">
            {SETTINGS_SECTIONS.map((section) => (
                <div className="sunburst-settings-section" key={section.id}>
                    <div className="sunburst-settings-section-title">{section.title}</div>
                    {section.fields.map((field) => (
                        <SettingsField key={field.key} field={field} settings={settings} onChange={onChange} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

const cellText = (col, room) => {
    if (col.key === 'has_awareness_poster') return room.has_awareness_poster ? 'Oui' : 'Non';
    if (col.key === 'frequentation') return FREQUENTATION_LABELS[room.frequentation] || room.frequentation || '-';
    return room[col.key] ?? '-';
};

const cellBadgeColor = (col, room) => {
    if (col.badge === 'rate') return RATE_COLORS[room.rate_letter] || '#ccc';
    if (col.badge === 'frequentation') return FREQUENTATION_COLORS[room.frequentation] || '#fff';
    return null;
};

// Mode Interactif : le <table> HTML d'origine (sémantique native), juste filtré sur
// les colonnes visibles.
const InteractiveTable = ({ data, visibleColumns }) => (
    <table className="rate-per-room-table">
        <tbody>
            <tr style={{ fontWeight: 'bold', textAlign: 'center' }}>
                {visibleColumns.map((col) => (
                    <td key={col.key} style={{ textAlign: col.align }}>
                        {col.label}
                    </td>
                ))}
            </tr>
            {data.map((room) => (
                <tr key={room._id} style={{ textAlign: 'center' }}>
                    {visibleColumns.map((col) => (
                        <td
                            key={col.key}
                            style={{
                                textAlign: col.align,
                                backgroundColor: cellBadgeColor(col, room) || undefined,
                                color: cellBadgeColor(col, room) ? '#000' : undefined,
                            }}
                        >
                            {cellText(col, room)}
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    </table>
);

// Estimation grossière de la largeur d'un texte (évite de mesurer le DOM) — même
// technique que UsageTaxonomySunburstCard.js pour dimensionner les badges de fréquentation
// (libellés "Moyenne"/"Faible" bien plus larges qu'une simple lettre de note).
const estimateTextWidth = (text, fontSize) => String(text).length * fontSize * 0.6 + 16;

// Mode Export : tableau dessiné en SVG à la main (pas de <table> HTML — buildExportSvg
// ne peut cloner qu'un vrai <svg>). Colonnes dimensionnées au prorata de `weight` parmi
// les colonnes visibles.
const ExportTable = ({ data, visibleColumns, settings }) => {
    const { tableWidth, rowHeight, fontSize } = settings;
    const headerHeight = rowHeight;
    const totalHeight = headerHeight + data.length * rowHeight;
    const totalWeight = visibleColumns.reduce((sum, c) => sum + c.weight, 0);

    let x = 0;
    const columnBoxes = visibleColumns.map((col) => {
        const width = (col.weight / totalWeight) * tableWidth;
        const box = { col, x, width };
        x += width;
        return box;
    });

    return (
        <svg width={tableWidth} height={totalHeight}>
            <rect x={0} y={0} width={tableWidth} height={headerHeight} fill={HEADER_BG} />
            {columnBoxes.map(({ col, x: colX, width }) => (
                <text
                    key={col.key}
                    x={col.align === 'left' ? colX + 10 : colX + width / 2}
                    y={headerHeight / 2}
                    textAnchor={col.align === 'left' ? 'start' : 'middle'}
                    dominantBaseline="central"
                    style={{ fontSize, fontWeight: 700, fontFamily: FONT_STACK, fill: DARK_TEXT }}
                >
                    {col.label}
                </text>
            ))}

            {data.map((room, rowIndex) => {
                const y = headerHeight + rowIndex * rowHeight;
                return (
                    <g key={room._id}>
                        <rect x={0} y={y} width={tableWidth} height={rowHeight} fill="none" stroke={BORDER_COLOR} strokeWidth={1} />
                        {columnBoxes.map(({ col, x: colX, width }) => {
                            const text = cellText(col, room);
                            const badgeColor = cellBadgeColor(col, room);
                            const cx = colX + width / 2;
                            const cy = y + rowHeight / 2;
                            if (badgeColor) {
                                const badgeWidth = Math.max(30, estimateTextWidth(text, fontSize));
                                return (
                                    <g key={col.key}>
                                        <rect x={cx - badgeWidth / 2} y={cy - 11} width={badgeWidth} height={22} rx={6} fill={badgeColor} />
                                        <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            style={{ fontSize, fontWeight: 700, fontFamily: FONT_STACK, fill: '#181c22' }}
                                        >
                                            {text}
                                        </text>
                                    </g>
                                );
                            }
                            return (
                                <text
                                    key={col.key}
                                    x={col.align === 'left' ? colX + 10 : cx}
                                    y={cy}
                                    textAnchor={col.align === 'left' ? 'start' : 'middle'}
                                    dominantBaseline="central"
                                    style={{ fontSize, fontFamily: FONT_STACK, fill: DARK_TEXT }}
                                >
                                    {text}
                                </text>
                            );
                        })}
                    </g>
                );
            })}

            {columnBoxes.slice(1).map(({ col, x: colX }) => (
                <line key={col.key} x1={colX} y1={0} x2={colX} y2={totalHeight} stroke={BORDER_COLOR} strokeWidth={1} />
            ))}
        </svg>
    );
};

/* ─────────────────────────────────────────────
   "Note par salle" — même patron Interactif/Export que les graphiques du dashboard,
   appliqué à un tableau. Contrairement aux graphiques (même composant Nivo dans les
   deux modes), les deux modes ont ici des rendus distincts : un vrai <table> HTML en
   Interactif, un tableau dessiné en SVG à la main en Export (un <table> ne peut pas
   être capturé par buildExportSvg, qui clone un <svg>).

   @param {object[]} data - dataConsumption.consumption_per_room
───────────────────────────────────────────── */
const RatePerRoomCard = ({ data }) => {
    const containerRef = useRef(null);
    const [mode, setMode] = useState('interactive'); // 'interactive' | 'export'
    const [columnVisibility, setColumnVisibility] = useState(() => Object.fromEntries(COLUMNS.map((c) => [c.key, true])));
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [panelOpen, setPanelOpen] = useState(false);
    const [downloadState, setDownloadState] = useState('idle');

    const updateSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
    const toggleColumn = (key) => setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
    const visibleColumns = COLUMNS.filter((c) => c.alwaysVisible || columnVisibility[c.key]);

    const handleDownloadSvg = () => {
        const svgEl = containerRef.current?.querySelector('svg');
        if (!svgEl) return;
        downloadSvg(buildExportSvg(svgEl), 'note-par-salle.svg');
        setDownloadState('downloaded');
        setTimeout(() => setDownloadState('idle'), 2000);
    };

    const modeToggle = (
        <SegmentedToggle
            ariaLabel="Mode d'affichage du tableau"
            value={mode}
            onChange={setMode}
            options={[
                { value: 'interactive', label: 'Interactif' },
                { value: 'export', label: 'Export' },
            ]}
        />
    );

    if (!data || data.length === 0) return null;

    return (
        <ChartCard
            title="Note par salle"
            actions={
                mode === 'export' ? (
                    <>
                        {modeToggle}
                        <ChartCardIconButton
                            onClick={() => setPanelOpen((v) => !v)}
                            title="Réglages d'affichage"
                            aria-label="Réglages d'affichage"
                        >
                            <FaSlidersH />
                        </ChartCardIconButton>
                        <ChartCardButton onClick={handleDownloadSvg} title="Télécharger le tableau en SVG">
                            {downloadState === 'downloaded' ? 'SVG téléchargé' : 'Télécharger en SVG'}
                        </ChartCardButton>
                    </>
                ) : (
                    modeToggle
                )
            }
            panel={mode === 'export' && panelOpen && <SettingsPanel settings={settings} onChange={updateSetting} onClose={() => setPanelOpen(false)} />}
        >
            <div className="chart-settings-bar">
                <div className="chart-settings-bar-group">
                    <p>Colonnes</p>
                    {COLUMNS.filter((c) => !c.alwaysVisible).map((col) => (
                        <label className="chart-settings-bar-checkbox" key={col.key}>
                            <input type="checkbox" checked={columnVisibility[col.key]} onChange={() => toggleColumn(col.key)} />
                            {col.label}
                        </label>
                    ))}
                </div>
            </div>

            {mode === 'interactive' ? (
                <InteractiveTable data={data} visibleColumns={visibleColumns} />
            ) : (
                <div ref={containerRef}>
                    <ExportTable data={data} visibleColumns={visibleColumns} settings={settings} />
                </div>
            )}
        </ChartCard>
    );
};

export default RatePerRoomCard;
